import { Diff } from 'diff';
import isNumber from 'lodash/isNumber';
import cloneDeep from 'lodash/cloneDeep';
import { LINE_BREAK_CHARACTER, SPACE } from './transcriptTextConstants';
import type TranscriptWord from './transcriptWord';

const wordDiff = new Diff();

// Splits by white space
wordDiff.tokenize = (value: string) => value.split(/(\s+)/);

function diffWordsWithSpace(oldStr: string, newStr: string) {
  const options: any = {};
  options['ignoreWhitespace'] = true;
  return wordDiff.diff(oldStr, newStr, options);
}

export interface DomRect {
  x: number;
  y: number;
  top: number;
  right: number;
  bottom: number;
  left: number;
  width: number;
  height: number;
}

export interface DomRectList {
  [index: number]: DomRect;
}

export interface SelectionOffsets {
  startOffset: number;
  endOffset: number;
}

export const BLANK_STRING = '';

export const getWordSeparator = (languageCode: string | undefined) => languageCode?.startsWith('th') ? BLANK_STRING : SPACE;

/**
 *
 * @param words array of words to calculate start and end times for
 * @param intervalStartTime start time of selection interval
 * @param intervalEndTime end time of selection interval
 * @returns array with timed words
 */
export const calculateWordTimes = (words: TranscriptWord[], intervalStartTime: number, intervalEndTime: number) => {
  const totalDuration = intervalEndTime - intervalStartTime;
  const totalCharacterCount = words.reduce((acc: number, cur: any) => acc + +cur.content.length, 0);

  let currentStartTime = 0;
  return words.map((w: any, i: number) => {
    let startTime = 0;
    let endTime = 0;
    if (i === 0) {
      startTime = intervalStartTime;
      endTime = Math.floor(startTime + (w.content.length / totalCharacterCount * 100) * totalDuration / 100);
      currentStartTime = endTime;
    } else if (i === words.length - 1) {
      endTime = intervalEndTime;
      startTime = Math.floor(intervalEndTime - (w.content.length / totalCharacterCount * 100) * totalDuration / 100);
    } else {
      startTime = currentStartTime;
      endTime = Math.floor(startTime + (w.content.length / totalCharacterCount * 100) * totalDuration / 100);
      currentStartTime = endTime;
    }
    return {
      ...w,
      startTime,
      endTime,
    };
  });
};

/**
 * Converts timed transcript array to raw text string
 * @param timedTranscript the timed transcript array
 * @returns raw transcript
 */
export const getRawTranscriptFromTimedTranscript = (timedTranscript: any[]) => {
  return timedTranscript.reduce((acc: string, cur: any) => `${acc} ${cur.content}`, '').trim();
};

export const getUpdatedTimedTranscript = (timedTranscript: any[], editedText: string) => {
  const currentTimedTranscript = cloneDeep(timedTranscript);
  const currentTranscript = getRawTranscriptFromTimedTranscript(currentTimedTranscript);
  const diff = diffWordsWithSpace(currentTranscript, editedText);
  const startTime = currentTimedTranscript[0].startTime;
  const endTime = currentTimedTranscript[currentTimedTranscript.length - 1].endTime;

  let index = 0;
  const updatedTimedTranscript: any[] = [];
  diff.forEach((d: any) => {
    if (d.added) {
      const newWords = d.value.split(' ');
      newWords.forEach((w: any) => {
        if (w) {
          updatedTimedTranscript.push({
              startTime: undefined,
              endTime: undefined,
              content: w,
              edited: true
          });
        }
      });
    } else if (d.removed) {
      const removedWords = d.value.split(' ');
      removedWords.forEach((w: any) => {
        if (w) {
          index += 1;
        }
      });
    } else {
      const words = d.value.split(' ');
      words.forEach((w: any) => {
        if (w) {
          if (currentTimedTranscript[index]) {
            updatedTimedTranscript.push(currentTimedTranscript[index]);
            index += 1;
          }
        }
      });
    }
  });

  const intervals: any[] = [];
  let interval: any = undefined;
  updatedTimedTranscript.forEach((t: any, i: number) => {
    // blank out to trigger retiming if words are deleted before or after inserted words.
    if (t.edited) {
      t.startTime = undefined;
      t.endTime = undefined;
    }

    if (!isNumber(t.startTime) && !interval && i !== updatedTimedTranscript.length - 1) {
      interval = {
        previous: i === 0 ? undefined : updatedTimedTranscript[i - 1],
        startIndex: i,
      };
    } else if (isNumber(t.startTime) && interval) {
      interval = { ...interval,  next: updatedTimedTranscript[i], endIndex: i - 1 };
      intervals.push({ ...interval });
      interval = undefined;
    } else if (i === updatedTimedTranscript.length - 1 && interval) {
      interval = { ...interval,  next: undefined, endIndex: i };
      intervals.push({ ...interval });
      interval = undefined;
    } else if (!isNumber(t.startTime) && i === updatedTimedTranscript.length - 1 && !interval) {
      interval = {
        previous: updatedTimedTranscript[i - 1],
        next: undefined,
        startIndex: i,
        endIndex: i,
      };
      intervals.push({ ...interval });
    }
  });

  intervals.forEach((int: any) => {
    const r = int.endIndex - int.startIndex;
    const length = (int.startIndex + r) + 1;
    const s = Math.floor(int.previous ? int.previous.endTime : startTime);
    const e = Math.floor(int.next ? int.next.startTime : endTime);
    const items: any[] = [];
    for (let i = int.startIndex; i < length; i++) {
      items.push(updatedTimedTranscript[i]);
    }
    const timed = calculateWordTimes(items, s, e);
    let ii = 0;
    for (let i = int.startIndex; i < length; i++) {
      updatedTimedTranscript[i] = timed[ii];
      ii += 1;
    }
  });
  return updatedTimedTranscript;
};

export const getOffsets = (): SelectionOffsets | undefined => {
  const selection = window.getSelection();
  if (selection === null) {
    throw 'Selection is null.';
  }
  if (selection.rangeCount > 0) {
    const range = selection.getRangeAt(0);
    return {
      startOffset: range.startOffset,
      endOffset: range.endOffset
    };
  }
  return undefined;
};


export const getRangeRect = (node: Node, startOffset: number, endOffset: number) => {
  if (node && node.textContent && startOffset > -1 && node.textContent.length >= endOffset) {
    const range = document.createRange();
    range.setStart(node, startOffset);
    range.setEnd(node, endOffset);
    const rect = range.getClientRects() as DomRectList;
    return rect[0] ? rect[0] : undefined;
  }
};


export const getParagraphIndexesBySelection = (element: HTMLElement) => {
  const selection = window.getSelection();
  if (
    selection &&
    selection.anchorNode &&
    selection.anchorNode.parentElement &&
    selection.focusNode &&
    selection.focusNode.parentElement
  ) {
    const paragraphs = element.querySelectorAll('.paragraph');
    const children = Array.from(paragraphs);

    // Fix for selection sometimes selection wrong element
    let anchor = selection.anchorNode.parentElement;
    let focused = selection.focusNode.parentElement;
    if (anchor.tagName === 'DIV') {
      anchor = selection.anchorNode as HTMLElement;
    }
    if (focused.tagName === 'DIV') {
      focused = selection.focusNode as HTMLElement;
    }

    let anchorIndex = children.indexOf(anchor);
    let focusIndex = children.indexOf(focused);

    if (anchorIndex > -1 && focusIndex > -1) {
      if (anchorIndex > focusIndex) {
        const temp = anchorIndex;
        anchorIndex = focusIndex;
        focusIndex = temp;
      }
      return {
        anchorIndex,
        focusIndex
      };
    } else {
      return undefined;
    }
  }
};


export const cleanInnerHtml = (value: string) => {
  // Convert `&amp;` to `&`.
  value = value.replace(/&amp;/gi, '&');

  // Replace spaces.
  value = value.replace(/&nbsp;/gi, ' ');
  value = value.replace(/\s+/g, ' ');

  // Remove "<b>".
  value = value.replace(/<b>/gi, '');
  value = value.replace(/<\/b>/gi, '');

  // Remove "<strong>".
  value = value.replace(/<strong>/gi, '');
  value = value.replace(/<\/strong>/gi, '');

  // Remove "<i>".
  value = value.replace(/<i>/gi, '');
  value = value.replace(/<\/i>/gi, '');

  // Remove "<em>".
  value = value.replace(/<em>/gi, '');
  value = value.replace(/<\/em>/gi, '');

  // Remove "<u>".
  value = value.replace(/<u>/gi, '');
  value = value.replace(/<\/u>/gi, '');

  // Tighten up "<" and ">".
  value = value.replace(/>\s+/g, '>');
  value = value.replace(/\s+</g, '<');

  // Replace "<br>".
  value = value.replace(/<br>/gi, '');

  // Replace "<div>" (from Chrome).
  value = value.replace(/<div>/gi, '');
  value = value.replace(/<\/div>/gi, '');

  // Replace "<p>" (from IE).
  value = value.replace(/<p>/gi, '\n');
  value = value.replace(/<\/p>/gi, '');

  // No more than 2x newline, per "paragraph".
  // value = value.replace(/\n\n+/g, '\n\n');

  // Whitespace before/after.
  value = value.trim();

  return value;
};


export const cleanLineBreakCharacters = (value: string) => {
  const lineBreakRegex = new RegExp(`${LINE_BREAK_CHARACTER}\\s?`, 'gi');
  return value.replace(lineBreakRegex, '').trimRight();
};

export const scanAheadForEndOfWord = (startIndex: number, text: string) => {
  let remainingCharacters = '';
  for (let i = startIndex; i < text.length; i++) {
    const curr = text[i];
    if (curr === ' ') {
      break;
    }
    const next = text[i + 1];
    remainingCharacters += curr;
    if (next === ' ') {
      break;
    }
  }
  return remainingCharacters.length;
};

export function getRedactedText(text: string): string;
export function getRedactedText(text: string | undefined): string | undefined;
export function getRedactedText(text: string | undefined): string | undefined {
  if (text) {
    return `redacted(${text.length})`;
  }

  return text;
}
